import axios from 'axios';
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import constants from '../constants';
import { useAuth } from './authContext';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from './userContext';

interface Course {
  _id: string;
  type: string;
  title: string;
  description: string;
  trainer: string;
  totalLessons: number;
  completionPercentage?: number;
  planType: string;
  startDate: string;
  step: string;
  active: boolean;
  desktopImg: string;
  mobileImg: string;
  isCertificateAvailable: boolean;
  isPaidInternshipAvailable: boolean;
  groupLink?: string;
  menu?: string[];
}

interface Enroll {
  _id: string;
  title?: string;
  active?: boolean;
  isCertificateAvailable?: boolean;
  isPaidInternshipAvailable?: boolean;
  planType?: string;
  startDate?: string;
  step?: string;
  type?: string;
  groupLink?: string;
  activeDay?: number;
  totalDays?: number;
  updatedAt?: string;
  menu?: string[];
  courses?: Course[];
}

interface EnrollContextType {
  enroll: Enroll;
  updateEnroll: (newEnrollData: Enroll) => void;
  initializeEnroll:()=>void,
  loading:boolean
}

const STORAGE_KEYS = {
  COHORT: 'cohort',
  ALL_COHORTS: 'allCohorts',
  USER: 'user',
} as const;

const REFRESH_INTERVAL = 4; // hours

const EnrollContext = createContext<EnrollContextType | undefined>(undefined);

const fetchEnrollData = async (token: string) => {
  try {
    const response = await axios.get(`${constants.DOMAIN}/api/v2/enroll/`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data.response || [];
  } catch (error) {
    console.error("Error fetching enroll data:", error);
    return [];
  }
};

const refreshEnrollData = async (enrollId: string, token: string) => {
  try {
    const response = await axios.get(`${constants.DOMAIN}/api/v2/enroll/refresh/${enrollId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data.response;
  } catch (error) {
    console.error('Unable to Fetch Enroll:', error);
    return null;
  }
};

const shouldRefresh = (lastUpdateTime?: string) => {
  if (!lastUpdateTime) return true;
  const hoursDifference = (new Date().getTime() - new Date(lastUpdateTime).getTime()) / (1000 * 60 * 60);
  return hoursDifference >= REFRESH_INTERVAL;
};

export const EnrollProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const { getToken,currentUser } = useAuth();
  const { updateUser } = useUser();
  const [enroll, setEnroll] = useState<Enroll>({ _id: '' });
  const [loading,setLoading] = useState(false)

  const handleUserUpdate = (enrollData: any[]) => {
    if (!enrollData.length) return;
    
    const userDataString = sessionStorage.getItem(STORAGE_KEYS.USER);
    if (!userDataString) return;

    try {
      const userData = JSON.parse(decodeURIComponent(atob(userDataString)));
      const updatedUser = { ...userData, paymentStatus: 'PAID' };
      updateUser(updatedUser);
      sessionStorage.setItem(STORAGE_KEYS.USER, btoa(encodeURIComponent(JSON.stringify(updatedUser))));
    } catch (error) {
      console.error('Error updating user data:', error);
    }
  };

  const updateEnroll = (newEnrollData: Enroll) => {
    setEnroll(newEnrollData);
    sessionStorage.setItem(STORAGE_KEYS.COHORT, btoa(JSON.stringify(newEnrollData)));
  };

  const refetchQueries = async () => {
    const queryKeys = {
      LMS: ['lms'],
      ASSESSMENT: ['assessment'],
      INTERNSHIP: ['internship'],
      DONE: ['paidinternship']
    };

    const step = enroll.step;
    if (!step) return;

    const steps = Object.keys(queryKeys);
    const currentStepIndex = steps.indexOf(step);
    
    if (currentStepIndex === -1) return;

    for (let i = 0; i <= currentStepIndex; i++) {
      await queryClient.refetchQueries({ 
        queryKey: queryKeys[steps[i] as keyof typeof queryKeys], 
        type: 'active' 
      });
    }
  };

  const checkRedirect = () => {
    if (window.location.pathname.includes('program/dashboard') && (!enroll._id)) {
      window.location.href = '/v1/user/dashboard/course';
    }
  };

  const initializeEnroll = async () => {
    setLoading(true)
    let cohortData = sessionStorage.getItem(STORAGE_KEYS.COHORT);
    const allCohortsData = sessionStorage.getItem(STORAGE_KEYS.ALL_COHORTS);

    if (!cohortData) {
      checkRedirect();
      cohortData = btoa(JSON.stringify(enroll))
    }

    const userData: Enroll = JSON.parse(atob(cohortData));
    console.log(allCohortsData);
    
    if (false) {
      // const courseData = JSON.parse(allCohortsData);
      // if (shouldRefresh(courseData.lastRefresh)) {
      //   const token = await getToken();
      //   const freshData = await fetchEnrollData(token);
      //   handleUserUpdate(freshData);
      //   updateEnroll({ ...userData, courses: freshData });
      //   if(freshData?.length > 0){
      //     sessionStorage.setItem(STORAGE_KEYS.ALL_COHORTS, JSON.stringify({
      //       lastRefresh: new Date(),
      //       programs: freshData
      //     })); }
      // } else {
      //   updateEnroll({ ...userData, courses: courseData.programs });
      // }
    } else {
      const token = await getToken();
      const freshData = await fetchEnrollData(token);
      handleUserUpdate(freshData);
      updateEnroll({ ...userData, courses: freshData });
      // if(freshData?.length > 0){
      // sessionStorage.setItem(STORAGE_KEYS.ALL_COHORTS, JSON.stringify({
      //   lastRefresh: new Date(),
      //   programs: freshData
      // })); }
    }
    setLoading(false)
    if (shouldRefresh(userData.updatedAt)) {
      const token = await getToken();
      const refreshedData = await refreshEnrollData(userData._id, token);
      if (refreshedData) {
        updateEnroll({
          ...refreshedData,
          courses:enroll.courses,
          updatedAt: new Date().toISOString()
        });
      }
    } else {
      setEnroll(userData);
    }

  };

  useEffect(() => {
    initializeEnroll();
  }, [currentUser]);

  useEffect(() => {
    if (enroll._id) {
      refetchQueries();
    }
  }, [enroll._id]);

  return (
    <EnrollContext.Provider value={{ enroll, updateEnroll,initializeEnroll,loading }}>
      {children}
    </EnrollContext.Provider>
  );
};

export const useEnroll = () => {
  const context = useContext(EnrollContext);
  if (!context) {
    throw new Error('useEnroll must be used within an EnrollProvider');
  }
  return context;
};